import type { Language } from '@eformulieren/engine'
import { StrictMode, useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { FormPage } from './FormPage.js'
import { instantieFromPath, readContext, prefillFromQuerystring, tabStorage } from './formContext.js'
import { beginContextState, getWhoami, afterWhoami } from './contextState.js'
import { LoginPage } from './LoginPage.js'
import { ENTRIES_RETRY_WITHOUT_SESSION, getEntriesWithRetry, withoutSession } from './login.js'
import { targetKeepingPrefill } from './prefillStash.js'
import { InvitationPage } from './InvitationPage.js'
import { type CodeStorage, codeOnArrival, isInvitationPath } from './invitation.js'
import { instantieFromAdviserPath } from './adviserRoute.js'
import { AdviserPage } from './AdviserPage.js'
import '@eformulieren/renderer/stijl/tokens.css'
import '@eformulieren/renderer/stijl/schil.css'
import 'katex/dist/katex.min.css'
// De bewerk-UI van een formule (R-14). MathLive's package levert deze CSS met relatieve
// `url()`-verwijzingen naar zijn eigen lettertypebestanden (`mathlive/fonts/*.woff2`); Vite
// herschrijft en bundelt ze net als bij elke andere geïmporteerde stylesheet, dus dit is al
// zelf-hosten en geen runtime-CDN, zonder een handmatige `public/`-map zoals CitatenPOC heeft.
import 'mathlive/fonts.css'
// Self-hosted, geen runtime-CDN (docs/bron/designsysteem/nwo.tokens.json:29). De gewichten
// zelf en niet `latin-400.css` e.d.: dat subset mist latin-ext en laat é/ë/ï alsnog op het
// systeemfont terugvallen.
import '@fontsource/barlow/400.css'
import '@fontsource/barlow/500.css'
import '@fontsource/barlow/600.css'
import '@fontsource/barlow/700.css'
// Idem voor het kopjeslettertype: alleen 400/600/700, de gewichten die de schil gebruikt.
import '@fontsource/saira-extra-condensed/400.css'
import '@fontsource/saira-extra-condensed/600.css'
import '@fontsource/saira-extra-condensed/700.css'
import './pagina.css'

/**
 * De ingang van de pagina die de pod serveert (ontwerp 3.2, B2, 11.1).
 *
 * **Dit is een uitleverbaar artefact en geen dev-pagina.** Hij wordt gebouwd
 * (`pnpm --filter @eformulieren/runtime bouw-pagina`), gaat mee in het image van de
 * publicatie, en de pod serveert hem op zijn image-digest (R-101, D-28). Daarmee is de
 * derde poortregel van M5 te halen.
 *
 * **De weergavemodus volgt uit de instantie, tenzij de context er een noemt.** De zeven
 * gedaanten zijn een prop van de renderer (R-138a, B7). Staat er een `modus` in `window.__proef`
 * of `sessionStorage`, dan wint die (een proef, de e2e-flows). Anders leidt `FormPage` hem
 * per instantie af uit wat `GET /projectie` meegeeft: open of bevroren, de laag die aan zet is,
 * of er een voorganger is, en de rol en kant van wie er kijkt (`gedaante.ts`). Tot deze opdracht
 * stond hier dat de pagina dat niet mocht, omdat het "beleid van het processysteem" zou zijn;
 * dat was van vóór M9. Sinds M9 is elke beurt een eigen instantie en kiest het processysteem de
 * gedaante al door de instantie aan te maken (`docs/koppelvlak.md` hoofdstuk 4). Een toetser die
 * via een directe link binnenkomt kreeg daardoor `invullen`, met een "Indienen" dat de pod
 * weigerde; zie `docs/dekking.md`, "De gedaante volgt uit de instantie".
 *
 * **`persoon` komt sinds deze opdracht ook van een directe link zonder eigen basis.** Zonder
 * `window.__proef` en zonder `sessionStorage`, maar met een instantie in het pad, haalt deze
 * pagina de persoon op bij `GET /whoami` (`leesendpoints.ts`) voordat ze de context als compleet
 * beschouwt: dat endpoint vertaalt het HttpOnly-sessiecookie dat de invuller na het inloggen
 * heeft gekregen naar dezelfde `persoon` die de pod ook voor elke schrijfactie gebruikt. Dat was
 * tot deze opdracht de openstaande vraag van M5.16d. De stand tijdens dat ene extra verzoek
 * (`'wachten'`, `contextstand.ts`) toont niet de "geen context"-melding en niet een leeg
 * formulier (valkuil 6): de instantie staat al vast, alleen wie ernaar kijkt nog niet.
 *
 * **Zonder sessie een knop per ingang naar de inlogdienst** (opdracht 5c, `inloggen.ts`). Geeft
 * `/whoami` een 401, dan vraagt de pagina de pod langs welke ingangen er in te loggen is, en de
 * inlogdienst stuurt na het inloggen terug naar dit adres. Tot die opdracht stond hier dan de
 * "geen context"-melding en kwam niemand verder. Bij een 503 of zonder verbinding helpt inloggen
 * niet, en dan zegt de pagina dat het nu niet lukt, met een knop om het opnieuw te proberen.
 *
 * **De taal staat op Nederlands en is te wisselen.** De wissel zit in de schil (R-144), dus
 * hier alleen de beginwaarde.
 */

/**
 * `sessionStorage` van dit tabblad, of een opslag die niets bewaart als de browser hem weigert
 * (geblokkeerde sitegegevens): dan werkt de pagina, alleen overleeft de code het inloggen niet.
 */
function sessionStorage(): CodeStorage {
  try {
    return window.sessionStorage
  } catch {
    return { getItem: () => null, setItem: () => undefined, removeItem: () => undefined }
  }
}

/**
 * De link uit een uitnodiging (`/uitnodiging#<code>`, R-64c, `uitnodiging.ts`).
 *
 * **Vóór React en vóór enig verzoek**: de code gaat meteen uit de adresbalk, met
 * `history.replaceState` en zonder navigatie, zodat ze niet in de geschiedenis blijft staan, niet
 * in een bladwijzer belandt en niet meegaat als iemand het adres kopieert. Ze blijft in dit
 * tabblad tot er een antwoord is, zodat ze een inlog en een herlaadslag overleeft.
 */
const INVITATION = ((): { readonly code: string | undefined; readonly opslag: CodeStorage } | undefined => {
  if (!isInvitationPath(window.location.pathname)) return undefined
  const storage = sessionStorage()
  const code = codeOnArrival(window.location.hash, storage)
  if (window.location.hash !== '') {
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`)
  }
  // Een tweede link in hetzelfde tabblad verandert alleen het fragment, en dan laadt de browser de
  // pagina niet opnieuw. Dan opnieuw laden, zodat hierboven de nieuwe code wordt gelezen en gewist.
  window.addEventListener('hashchange', () => {
    window.location.reload()
  })
  return { code, opslag: storage }
})()

/**
 * De adviseurpagina (`/<instantie>/advies`): de instantie uit dat pad. Zij gaat door dezelfde vaststelling
 * van wie er kijkt als het formulier, met inloggen als er geen sessie is; het doel van het inloggen is
 * dan dit pad, zodat de adviseur na inloggen terugkomt waar hij was.
 */
const ADVISER_INSTANTIE = instantieFromAdviserPath(window.location.pathname)

function startContext() {
  const base = readContext()
  if (ADVISER_INSTANTIE === undefined) return beginContextState(base, instantieFromPath())
  return beginContextState(base === undefined ? undefined : { ...base, instantieId: ADVISER_INSTANTIE }, ADVISER_INSTANTIE)
}

function Page() {
  const [language, setLanguage] = useState<Language>('nl')
  // Lazy, zodat de synchrone basis (window.__proef, sessionStorage) meteen bij de eerste render
  // vaststaat en er geen renderslag verloren gaat waar vóór deze opdracht ook geen was.
  const [state, setState] = useState(startContext)

  useEffect(() => {
    if (state.soort !== 'wachten') return
    let cleaned = false
    const instantieId = state.instantieId
    void (async () => {
      const outcome = await getWhoami()
      if (outcome.soort === 'geen-sessie') {
        // Begrensd opnieuw geprobeerd: één haperend verzoek is nog geen storing (`getEntriesWithRetry`).
        const entries = await getEntriesWithRetry(ENTRIES_RETRY_WITHOUT_SESSION, { cancelled: () => cleaned })
        if (cleaned) return
        // Het adres van de pagina, en is dat met de voorvulling te lang voor het flowcookie van de
        // inlogdienst, dan zonder query en met de voorvulling in dit tabblad (`prefillStash.ts`).
        const target = targetKeepingPrefill(
          `${window.location.origin}${window.location.pathname}`,
          window.location.search,
          instantieId,
          tabStorage(),
          Date.now(),
        )
        setState(withoutSession(entries, target))
        return
      }
      if (cleaned) return
      setState(afterWhoami(instantieId, outcome, prefillFromQuerystring()))
    })()
    return () => {
      cleaned = true
    }
  }, [state])

  if (INVITATION !== undefined) {
    return <InvitationPage taal={language} onLanguage={setLanguage} code={INVITATION.code} opslag={INVITATION.opslag} />
  }

  if (state.soort === 'wachten') {
    // Nooit "geen context" en nooit een leeg formulier tijdens dit ene verzoek (valkuil 6): de
    // instantie staat al vast, alleen wie ernaar kijkt nog niet.
    return (
      <p className="pod-melding" role="status" data-proef="wachten-op-persoon">
        Bezig met bepalen wie er invult.
      </p>
    )
  }

  if (state.soort === 'inloggen' || state.soort === 'storing') {
    const instantieId = ADVISER_INSTANTIE ?? instantieFromPath()
    return (
      <LoginPage
        taal={language}
        onLanguage={setLanguage}
        stand={state}
        // Opnieuw vaststellen wie er kijkt; zonder instantie in het pad komt de pagina hier niet.
        onAgain={() => setState(instantieId === undefined ? { soort: 'geen-context' } : { soort: 'wachten', instantieId })}
      />
    )
  }

  if (state.soort === 'geen-context') {
    /*
      Geen context betekent: er is geen instantie in het pad en niemand die zegt welke. Dan staat
      er wat er mankeert en niet een leeg formulier:
      een formulier zonder antwoorden ziet er hetzelfde uit als een formulier waarvan de
      gegevens weg zijn (valkuil 6).
    */
    return (
      <p className="pod-melding" role="alert" data-proef="geen-context">
        Dit formulier wordt geopend vanuit een portaal, een medewerkersomgeving, of een directe
        link na inloggen. Die geeft mee wie er invult en om welke aanvraag het gaat; zonder dat
        is er niets te tonen.
      </p>
    )
  }

  if (ADVISER_INSTANTIE !== undefined) {
    // De adviseurpagina, waar aannemen van een adviesvraag heen gaat.
    return <AdviserPage taal={language} onLanguage={setLanguage} context={state.context} />
  }

  return (
    <FormPage
      context={state.context}
      taal={language}
      onLanguage={setLanguage}
      {...(state.context.modus === undefined ? {} : { mode: state.context.modus })}
    />
  )
}

createRoot(document.getElementById('formulier') as HTMLElement).render(
  <StrictMode>
    <Page />
  </StrictMode>,
)
